import { useMemo, useRef, useState } from 'react';
import Highcharts from 'highcharts/highstock';
import HighchartsReact from 'highcharts-react-official';
import styles from './MarketHealth.module.css';
import { ZONE_COLORS } from './riskScale';
import { toSortedPoints } from './chartDateUtils';
import { baseChartOptions, baseXAxis, baseYAxis, RANGE_PRESETS, WEEKLY_DATA_GROUPING } from './chartTheme';
import { selectLag } from './strategyLagSelect';
import { MODE_HISTORY_FIELD } from './strategyTransitions';
import { computeWindowStats, buildReturnStatRows } from './strategyStats';
import PerformanceStatsTable from './PerformanceStatsTable';

const START_VALUE = 10000;
// Below this many points, Highcharts' navigator/range-selector combo (built
// for real ranges) doesn't add anything and can look broken with 0-1
// points — fall back to a plain message per spec §10's bootstrap guidance.
const MIN_POINTS_FOR_CHART = 2;

// Builds Highcharts `zones` (zoneAxis: 'x') so the strategy-equity line
// segments render green while invested and red while in cash (spec §9.7:
// "green when invested, red when cash — use Highcharts zones or segment
// coloring by strategyPosition"). Each zone's `value` is the boundary
// timestamp; a point is assigned to the first zone whose value is >= its x,
// so the boundary is placed 1ms before the first point of the NEW segment —
// otherwise that point would still fall in the outgoing zone.
const buildPositionZones = (points, modeField, lag) => {
  if (points.length === 0) return undefined;
  const zones = [];
  let currentPosition = selectLag(points[0][modeField], lag)?.position;
  for (let i = 1; i < points.length; i++) {
    const position = selectLag(points[i][modeField], lag)?.position;
    if (position !== currentPosition) {
      zones.push({
        value: points[i].ts - 1,
        color: currentPosition === 'invested' ? ZONE_COLORS.green : ZONE_COLORS.red,
      });
      currentPosition = position;
    }
  }
  zones.push({ color: currentPosition === 'invested' ? ZONE_COLORS.green : ZONE_COLORS.red });
  return zones;
};

// Series values are indexed to START_VALUE ($10,000), so they read naturally
// as dollars. Whole-dollar rounding (no cents) with thousands separators — the
// index level, not a precise quote, so cents would be false precision. Uses
// en-US grouping to match the '$10,000' axis title and the rest of the app.
const CURRENCY_FORMAT = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});
const formatCurrency = (value) => (Number.isFinite(value) ? CURRENCY_FORMAT.format(value) : '—');

// Position ('invested'/'cash') labels the strategy line's green/red zones
// (spec §9.7) in the tooltip as a single word — the composite score is no
// longer shown here.
const POSITION_LABEL = { invested: 'Invested', cash: 'Cash' };

/**
 * S&P 500 + Market Health strategy chart (spec §9.7): a Highcharts Stock
 * chart (for the range selector + navigator) with two comparable series —
 * SPY buy-and-hold and the strategy's equity curve, both normalized to
 * start at 10000 on the same date — plus the strategy summary line (trades,
 * total return, CAGR, volatility, Sharpe, Martin Ratio, and max drawdown,
 * each vs. SPY buy-and-hold) below it, per §9.8's layout, and a "when this
 * model lags" callout (spec §5) with equal visual weight to the stats table.
 *
 * Only one y-axis is used (not the two the §9.7 heading names but never
 * describes a second bullet for) — both series are index values on the
 * same 10000-start scale specifically so they're visually comparable, which
 * a split axis would defeat.
 *
 * `mode` ('responsive'|'patient', market-health-v2-spec.md §2.1/§2.3)
 * selects which per-mode fields to read: `history[i].strategy` + capitalized
 * mode (e.g. `strategyResponsive`) for the equity curve and position zones,
 * and `strategy[mode]` for the stats table below the chart.
 *
 * Per spec §8, the built-in Highcharts `rangeSelector` is disabled in favor
 * of a custom button row (RANGE_PRESETS, shared with DrawdownChart via
 * chartTheme.js) that jumps the x-axis to fixed historical windows —
 * including the unflattering ones (2018 Vol, 2022 Bear), not just the
 * flattering 2020 COVID rebound.
 */
const StrategyChart = ({ history = [], strategy, mode, lag = false }) => {
  const modeField = MODE_HISTORY_FIELD[mode] || MODE_HISTORY_FIELD.adaptive;
  const modeStrategy = selectLag(strategy?.[mode], lag) ?? null;
  const sorted = useMemo(() => toSortedPoints(history, 'spy'), [history]);
  // Populated via HighchartsReact's `callback` prop (invoked with the chart
  // instance once Highcharts constructs it) — same idiom as DrawdownChart's
  // range-preset buttons (spec §8), which this mirrors.
  const chartRef = useRef(null);
  const [selectedPreset, setSelectedPreset] = useState(0);

  if (sorted.length === 0) {
    return (
      <p className={styles.statusMessage}>
        Strategy history begins once the first daily score is computed. Check back after
        the next daily run.
      </p>
    );
  }

  if (sorted.length < MIN_POINTS_FOR_CHART) {
    return (
      <p className={styles.statusMessage}>
        Only one day of history so far ({sorted[0].date}) — the strategy chart needs at
        least two days of data. Check back tomorrow.
      </p>
    );
  }

  // Crash presets (RANGE_PRESETS with `subset: true`) FILTER the data down to
  // the episode window rather than just zooming the axis: the navigator then
  // spans only the crash, so the user can pan/zoom within it but can't scroll
  // back out to full history. The filtered series are also re-based to
  // START_VALUE at the subset's first point, so SPY and the strategy start
  // together at the same value for that period. 'All'/'Trailing NY' carry no
  // `subset` flag — they keep the full dataset with day-one indexing and let
  // the navigator explore the whole range.
  const activePreset = RANGE_PRESETS[selectedPreset];
  const windowed = (() => {
    if (!activePreset?.subset) return sorted;
    const [min, max] = activePreset.getExtremes();
    return sorted.filter((d) => d.ts >= min && d.ts <= max);
  })();

  // SPY series: divide by the first in-window price so the subset's first day
  // reads exactly START_VALUE (for 'All'/'Trailing NY' the subset IS the full
  // series, so this anchors to day one — the prior behavior).
  const spyPoints = windowed.filter((d) => typeof d.spy === 'number');
  const baseSpy = spyPoints[0]?.spy;
  const spySeries = baseSpy
    ? spyPoints.map((d) => [d.ts, (d.spy / baseSpy) * START_VALUE])
    : [];

  // Strategy series: same idiom against the equity curve, anchored to the
  // subset's first equity point.
  const equityPoints = windowed.filter((d) => typeof selectLag(d[modeField], lag)?.equity === 'number');
  const baseEquity = selectLag(equityPoints[0]?.[modeField], lag)?.equity;
  const strategySeries = baseEquity
    ? equityPoints.map((d) => [d.ts, (selectLag(d[modeField], lag).equity / baseEquity) * START_VALUE])
    : [];

  const zones = buildPositionZones(windowed, modeField, lag);

  // Stats table reads from the backend's PRECOMPUTED per-preset windows
  // (strategy[mode][lag].windows[presetKey]), which are computed over the FULL
  // DAILY series — daily-accurate for every range preset. Falls back to
  // recomputing from the weekly-downsampled `windowed` slice for older payloads
  // that predate the `.windows` field (same pattern DrawdownChart uses).
  const windowStats = strategy?.[mode]?.[lag ? 'lagOn' : 'lagOff']?.windows?.[activePreset?.key];
  const windowStatRows = buildReturnStatRows(
    windowStats ?? computeWindowStats(windowed, modeField, lag),
  );

  // dataGrouping (weekly-and-coarser when zoomed out) means a drawn point's
  // `index` no longer maps into `sorted`, so the shared tooltip keys off the
  // hovered point's timestamp (`this.x`) instead. This map makes that O(1);
  // the series group by 'high', so `this.x` is the highest-value day in the
  // bucket, present verbatim here.
  const rowByTs = new Map(windowed.map((d) => [d.ts, d]));

  const handlePresetClick = (preset, index) => {
    setSelectedPreset(index);
    const xAxis = chartRef.current?.xAxis?.[0];
    if (!xAxis) return;
    if (preset.subset) {
      // Data is filtered to the window on re-render (see `windowed`), so the
      // series already bound the navigable range — clear any leftover extremes
      // from a previous relative preset so the whole (now-subset) series shows.
      xAxis.setExtremes(undefined, undefined);
      return;
    }
    // Relative presets ('Trailing NY') keep the full dataset and just move the
    // visible window; 'All' clears extremes entirely.
    const [min, max] = preset.getExtremes();
    xAxis.setExtremes(min ?? undefined, max ?? undefined);
  };

  const chartOptions = {
    ...baseChartOptions,
    rangeSelector: { enabled: false },
    navigator: { enabled: true },
    scrollbar: { enabled: false },
    xAxis: { ...baseXAxis },
    yAxis: {
      ...baseYAxis,
      title: { ...baseYAxis.title, text: 'Value (indexed to $10,000)' },
      opposite: false,
    },
    legend: { ...baseChartOptions.legend, enabled: true },
    tooltip: {
      ...baseChartOptions.tooltip,
      shared: true,
      xDateFormat: '%Y-%m-%d',
      formatter() {
        // Look the row up by the hovered point's timestamp rather than its
        // series index — grouping-safe (see rowByTs comment above).
        const ts = this.points?.[0]?.point?.x ?? this.x;
        const row = rowByTs.get(ts);
        const dateLabel = row?.date ?? Highcharts.dateFormat('%Y-%m-%d', ts);
        const lines = [`<b>${dateLabel}</b>`];
        this.points.forEach((p) => {
          lines.push(`${p.series.name}: ${formatCurrency(p.y)}`);
        });
        // Position names the strategy line's green/red color; the composite
        // score is intentionally omitted here so the tooltip stays focused on
        // the two equity values plus the invested/cash state.
        const position = selectLag(row?.[modeField], lag)?.position;
        const positionLabel = POSITION_LABEL[position];
        if (positionLabel) {
          lines.push(positionLabel);
        }
        return lines.join('<br/>');
      },
    },
    plotOptions: {
      series: { dataGrouping: WEEKLY_DATA_GROUPING },
    },
    series: [
      {
        name: 'Buy & Hold SPY',
        data: spySeries,
        color: '#6c757d',
        lineWidth: 1.5,
      },
      {
        name: 'Market Health Strategy',
        data: strategySeries,
        color: ZONE_COLORS.green,
        lineWidth: 2,
        zoneAxis: 'x',
        zones,
      },
    ],
  };

  return (
    <div>
      <div className={styles.rangePresets} role="group" aria-label="Strategy chart date range">
        {RANGE_PRESETS.map((preset, index) => {
          const active = selectedPreset === index;
          return (
            <button
              key={preset.text}
              type="button"
              className={active ? styles.rangePresetButtonActive : styles.rangePresetButton}
              aria-pressed={active}
              onClick={() => handlePresetClick(preset, index)}
            >
              {preset.text}
            </button>
          );
        })}
      </div>
      <HighchartsReact
        highcharts={Highcharts}
        constructorType="stockChart"
        options={chartOptions}
        callback={(chart) => { chartRef.current = chart; }}
      />
      {modeStrategy && (
        <PerformanceStatsTable
          rows={windowStatRows}
          strategyLabel="Strategy"
          benchmarkLabel="SPY Buy & Hold"
          lead={(
            <p className={styles.strategyLead}>
              Roughly half SPY&apos;s worst drawdown, with comparable return over full cycles.{' '}
              <strong>{modeStrategy.trades ?? 0}</strong> trades since{' '}
              <strong>{modeStrategy.startDate ?? sorted[0].date}</strong>.{' '}
              <span className={styles.strategyLeadWindow}>Stats reflect the selected range.</span>
            </p>
          )}
        />
      )}
    </div>
  );
};

export default StrategyChart;
